import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { useSubscription } from '@/hooks/use-subscription';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const DISMISS_KEY = 'payment_failed_banner_dismissed_session';
const FAILED_PAYMENT_STATUSES = new Set(['past_due', 'unpaid', 'incomplete', 'incomplete_expired']);

export const PaymentFailedBanner = () => {
  const { user } = useAuth();
  const { status, loading, checkSubscription } = useSubscription();
  const { toast } = useToast();
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === 'true',
  );
  const [openingPortal, setOpeningPortal] = useState(false);
  const visible = Boolean(user && !loading && !dismissed && FAILED_PAYMENT_STATUSES.has(status));

  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    if (!visible) return;

    refreshIntervalRef.current = setInterval(() => {
      void checkSubscription();
    }, 30_000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [checkSubscription, visible]);

  if (!visible) {
    return null;
  }

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  const handleUpdateCard = async () => {
    setOpeningPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (!data?.url) throw new Error('No customer portal URL returned');
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: 'Error',
        description: 'Failed to open billing settings. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setOpeningPortal(false);
    }
  };

  return (
    <div
      role="alert"
      className="sticky top-0 z-50 border-b border-red-200 bg-amber-50 px-4 py-3 text-red-950 shadow-sm dark:border-red-900 dark:bg-red-950 dark:text-red-50"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 text-sm">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-amber-400" />
          <p className="font-medium">
            Your last payment didn't go through. Update your card to keep your Pro access.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 pl-8 sm:pl-0">
          <Button
            type="button"
            size="sm"
            onClick={handleUpdateCard}
            disabled={openingPortal}
            className="bg-red-700 text-white hover:bg-red-800 dark:bg-amber-500 dark:text-red-950 dark:hover:bg-amber-400"
          >
            {openingPortal ? 'Opening...' : 'Update card'}
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label="Dismiss payment warning"
            onClick={handleDismiss}
            className="h-9 w-9 text-red-700 hover:bg-red-100 hover:text-red-900 dark:text-red-100 dark:hover:bg-red-900"
          >
            <X />
          </Button>
        </div>
      </div>
    </div>
  );
};
